import React, { useState } from 'react';
import {
  Card, Button, InputNumber, Space, Typography, Row, Col,
  Statistic, Tag, Divider, List, Popconfirm, Alert, Switch,
  Avatar, Progress
} from 'antd';
import {
  PlusOutlined, CheckCircleOutlined, UndoOutlined,
  TrophyOutlined, FireOutlined, HeartFilled, SwapOutlined,
  CrownOutlined
} from '@ant-design/icons';
import { getStufeInfo } from '../../api/services/equalOffenseService';

const { Title, Text } = Typography;

/**
 * EqualOffenseMatchplay Component
 * UI for tracking points during a 2-player Equal Offense match
 */
const EqualOffenseMatchplay = ({
  session,
  player1,
  player2,
  player1Frames = [],
  player2Frames = [],
  onAddFrame,
  onDeleteFrame,
  onFinishSession,
  loading = false,
}) => {
  const [activePlayer, setActivePlayer] = useState(1); // 1 or 2
  const [points, setPoints] = useState(0);
  const [livesUsed, setLivesUsed] = useState(0);
  const [foul, setFoul] = useState(false);

  const stufeInfo = getStufeInfo(session?.stufe);

  // Calculate stats for both players
  const player1Stats = {
    totalPoints: player1Frames.reduce((sum, f) => sum + f.points, 0),
    highRun: Math.max(0, ...player1Frames.map(f => f.points)),
    innings: player1Frames.length,
  };
  player1Stats.average = player1Stats.innings > 0
    ? (player1Stats.totalPoints / player1Stats.innings).toFixed(1)
    : 0;

  const player2Stats = {
    totalPoints: player2Frames.reduce((sum, f) => sum + f.points, 0),
    highRun: Math.max(0, ...player2Frames.map(f => f.points)),
    innings: player2Frames.length,
  };
  player2Stats.average = player2Stats.innings > 0
    ? (player2Stats.totalPoints / player2Stats.innings).toFixed(1)
    : 0;

  // Lives calculation
  const maxLives = stufeInfo?.value <= 3 ? (4 - stufeInfo.value) : (7 - stufeInfo.value);

  // Determine leader
  const leader = player1Stats.totalPoints > player2Stats.totalPoints ? 1
    : player2Stats.totalPoints > player1Stats.totalPoints ? 2
    : 0;

  const handleAddFrame = () => {
    const currentPlayer = activePlayer === 1 ? player1 : player2;
    const frameData = {
      session: session.id,
      player: currentPlayer.id,
      points,
      lives_used: livesUsed,
      foul,
    };
    onAddFrame(frameData);

    // Reset and switch player
    setPoints(0);
    setLivesUsed(0);
    setFoul(false);
    setActivePlayer(activePlayer === 1 ? 2 : 1);
  };

  const quickAddButtons = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 14, 15];

  const PlayerCard = ({ player, stats, playerNum, isActive }) => (
    <Card
      style={{
        borderColor: isActive ? '#1890ff' : undefined,
        borderWidth: isActive ? 2 : 1,
        background: isActive ? '#e6f7ff' : undefined,
      }}
      size="small"
    >
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Space>
          <Avatar style={{ background: playerNum === 1 ? '#1890ff' : '#52c41a' }}>
            {player?.name?.charAt(0) || '?'}
          </Avatar>
          <Text strong style={{ fontSize: 16 }}>
            {player?.name || `Spieler ${playerNum}`}
          </Text>
          {leader === playerNum && (
            <CrownOutlined style={{ color: '#faad14', fontSize: 18 }} />
          )}
          {isActive && <Tag color="blue">Am Zug</Tag>}
        </Space>

        <Row gutter={8}>
          <Col span={12}>
            <Statistic
              title="Punkte"
              value={stats.totalPoints}
              valueStyle={{
                color: playerNum === 1 ? '#1890ff' : '#52c41a',
                fontSize: 24
              }}
            />
          </Col>
          <Col span={12}>
            <Statistic
              title="High Run"
              value={stats.highRun}
              prefix={<FireOutlined />}
              valueStyle={{ fontSize: 18 }}
            />
          </Col>
        </Row>

        <Row gutter={8}>
          <Col span={12}>
            <Text type="secondary">Aufnahmen: {stats.innings}</Text>
          </Col>
          <Col span={12}>
            <Text type="secondary">Ø {stats.average}</Text>
          </Col>
        </Row>
      </Space>
    </Card>
  );

  return (
    <div>
      {/* Player Comparison Header */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12}>
          <PlayerCard
            player={player1}
            stats={player1Stats}
            playerNum={1}
            isActive={activePlayer === 1}
          />
        </Col>
        <Col xs={12}>
          <PlayerCard
            player={player2}
            stats={player2Stats}
            playerNum={2}
            isActive={activePlayer === 2}
          />
        </Col>
      </Row>

      {/* Score Comparison Bar */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <Text strong>
            {player1Stats.totalPoints} - {player2Stats.totalPoints}
          </Text>
        </div>
        <Progress
          percent={
            player1Stats.totalPoints + player2Stats.totalPoints > 0
              ? (player1Stats.totalPoints / (player1Stats.totalPoints + player2Stats.totalPoints)) * 100
              : 50
          }
          showInfo={false}
          strokeColor="#1890ff"
          trailColor="#52c41a"
        />
        <Row justify="space-between">
          <Col><Text type="secondary">{player1?.name}</Text></Col>
          <Col><Text type="secondary">{player2?.name}</Text></Col>
        </Row>
      </Card>

      {/* Stufe Info */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          <Space>
            <Tag color={stufeInfo.value <= 3 ? 'green' : stufeInfo.value <= 6 ? 'orange' : 'red'}>
              Stufe {stufeInfo.value}
            </Tag>
            <Text>{stufeInfo.description}</Text>
          </Space>
        }
      />

      {/* Point Input for Active Player */}
      <Card
        title={
          <Space>
            <SwapOutlined />
            <Text>
              Aufnahme für{' '}
              <Text strong style={{ color: activePlayer === 1 ? '#1890ff' : '#52c41a' }}>
                {activePlayer === 1 ? player1?.name : player2?.name}
              </Text>
            </Text>
          </Space>
        }
        extra={
          <Button
            size="small"
            onClick={() => setActivePlayer(activePlayer === 1 ? 2 : 1)}
          >
            Spieler wechseln
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Text strong>Punkte:</Text>
            <InputNumber
              value={points}
              onChange={setPoints}
              min={0}
              max={150}
              size="large"
              style={{ width: '100%', marginTop: 8 }}
            />

            {/* Quick Add Buttons */}
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {quickAddButtons.map(p => (
                  <Button
                    key={p}
                    size="small"
                    type={points === p ? 'primary' : 'default'}
                    onClick={() => setPoints(p)}
                  >
                    {p}
                  </Button>
                ))}
              </div>
            </div>
          </Col>

          <Col xs={24} md={12}>
            {/* Lives Used */}
            {stufeInfo.value < 7 && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>Leben verbraucht:</Text>
                <div style={{ marginTop: 8 }}>
                  <Space>
                    {[0, 1, 2, 3].slice(0, maxLives + 1).map(l => (
                      <Button
                        key={l}
                        type={livesUsed === l ? 'primary' : 'default'}
                        danger={l > 0}
                        onClick={() => setLivesUsed(l)}
                      >
                        {l === 0 ? 'Keine' : (
                          <Space>
                            {[...Array(l)].map((_, i) => (
                              <HeartFilled key={i} style={{ color: '#ff4d4f' }} />
                            ))}
                          </Space>
                        )}
                      </Button>
                    ))}
                  </Space>
                </div>
              </div>
            )}

            {/* Foul Toggle */}
            <div>
              <Space>
                <Switch checked={foul} onChange={setFoul} />
                <Text type={foul ? 'danger' : 'secondary'}>Foul begangen</Text>
              </Space>
            </div>
          </Col>
        </Row>

        <Divider />

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddFrame}
          loading={loading}
          size="large"
          block
          style={{
            background: activePlayer === 1 ? '#1890ff' : '#52c41a',
            borderColor: activePlayer === 1 ? '#1890ff' : '#52c41a',
          }}
        >
          Aufnahme speichern ({points} Punkte)
        </Button>
      </Card>

      {/* Combined Frame History */}
      <Card
        title={`Spielverlauf (${player1Frames.length + player2Frames.length} Aufnahmen)`}
        extra={
          <Popconfirm
            title="Match beenden?"
            description="Möchtest du dieses Match wirklich beenden?"
            onConfirm={() => onFinishSession(leader === 1 ? player1?.id : player2?.id)}
            okText="Beenden"
            cancelText="Abbrechen"
          >
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              disabled={player1Frames.length === 0 && player2Frames.length === 0}
            >
              Match beenden
            </Button>
          </Popconfirm>
        }
      >
        {player1Frames.length === 0 && player2Frames.length === 0 ? (
          <Text type="secondary">Noch keine Aufnahmen. Startet mit der ersten Aufnahme!</Text>
        ) : (
          <List
            dataSource={[...player1Frames, ...player2Frames]
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            }
            renderItem={(frame) => {
              const isPlayer1 = frame.player === player1?.id;
              return (
                <List.Item
                  key={frame.id}
                  actions={[
                    <Popconfirm
                      key="delete"
                      title="Aufnahme löschen?"
                      onConfirm={() => onDeleteFrame(frame.id)}
                      okText="Ja"
                      cancelText="Nein"
                    >
                      <Button type="text" danger icon={<UndoOutlined />} size="small">
                        Rückgängig
                      </Button>
                    </Popconfirm>
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        size="small"
                        style={{ background: isPlayer1 ? '#1890ff' : '#52c41a' }}
                      >
                        {isPlayer1 ? player1?.name?.charAt(0) : player2?.name?.charAt(0)}
                      </Avatar>
                    }
                    title={
                      <Space>
                        <Text style={{ color: isPlayer1 ? '#1890ff' : '#52c41a' }}>
                          {isPlayer1 ? player1?.name : player2?.name}
                        </Text>
                        <Text strong>{frame.points} Punkte</Text>
                        {frame.lives_used > 0 && (
                          <Tag color="red">-{frame.lives_used} Leben</Tag>
                        )}
                        {frame.foul && <Tag color="orange">Foul</Tag>}
                      </Space>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </Card>
    </div>
  );
};

export default EqualOffenseMatchplay;
